import React, { useEffect, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label,
  FormGroup, Alert, Badge, Spinner, Row, Col,
} from "reactstrap";
import { settleResearch } from "../../services/userPredictionService";
import { modelNameOf } from "../../helpers/modelName";

/**
 * Settling a closed forecast, and every other answer to the same question.
 *
 * Member research is not scored automatically: that would mean buying the
 * closing price, and this project has no commercial market-data licence. The
 * member enters the close themselves.
 *
 * The whole prompt group is settled at once. A member asks several models the
 * same question in one go, and those answers share a baseline price and a
 * window, so they share an outcome. Asking for the same number once per model
 * would be five chances to fat-finger it and end up with models appearing to
 * disagree about what the market did.
 *
 * Three numbers and nothing else. The dialog used to also collect a star
 * rating per model, a set of "what happened" chips and a free-text note — all
 * optional, none of them read by the scorer, and between them the reason
 * settling a forecast felt like filling in a form. The close is the only thing
 * a score needs; the high and low add one fact, whether price traded inside a
 * range even though it closed outside.
 *
 * All three fields are shown at once. Hiding the high and low behind a link
 * saved a little height and cost a click on every settlement that used them.
 *
 * The API still accepts ratings, tags and a note, and rows that already carry
 * them keep them. They are simply no longer asked for here.
 */
const SettleModal = ({ isOpen, toggle, prediction, siblings = [], onSettled }) => {
  const [start, setStart] = useState("");   // the baseline, asked for here now
  const [price, setPrice] = useState("");
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setPrice("");
      setStart("");
      setLow("");
      setHigh("");
      setError(null);
    }
  }, [isOpen, prediction?.id]);

  if (!prediction) return null;

  // The rows this settlement will cover: the one clicked, plus every other
  // unsettled answer to the same prompt.
  const group = [prediction, ...siblings.filter((s) => s.id !== prediction.id)];

  // The baseline, asked for here rather than when the forecast was made.
  //
  // The prompt no longer tells the model what the asset is worth: finding that
  // out is part of what is being tested. Which leaves this the one place the
  // real number is established, and it is an easier question here than it was
  // there -- a price at a moment that has passed, readable off any chart, not
  // a live quote somebody had to catch.
  //
  // Prefilled from what the models reported when they agree closely enough to
  // be worth offering. That is a suggestion to check, not an answer: it comes
  // from the same models this number is used to grade.
  const reported = group
    .map((r) => Number(r.model_reported_price))
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
  const suggested = reported.length
    ? reported[Math.floor(reported.length / 2)]
    : null;
  // Only offered when the models broadly agree. A spread means at least one of
  // them was not looking at the market, and a median of that is not a price.
  const reportedSpread = reported.length > 1
    ? (reported[reported.length - 1] - reported[0]) / reported[0]
    : 0;
  const suggestion = suggested && reportedSpread <= 0.02 ? suggested : null;

  const startNum = Number(start);
  const validStart = Number.isFinite(startNum) && startNum > 0;
  const baseline = validStart ? startNum : Number(prediction.current_price);
  const typed = Number(price);
  const validPrice = Number.isFinite(typed) && typed > 0;

  const lowNum = low === "" ? null : Number(low);
  const highNum = high === "" ? null : Number(high);
  const windowPartial = (low === "") !== (high === "");
  const windowInverted = lowNum !== null && highNum !== null && lowNum > highNum;
  const closeOutside = lowNum !== null && highNum !== null && validPrice
    && (typed < lowNum || typed > highNum);
  const windowOk = !windowPartial && !windowInverted && !closeOutside;

  const localError = windowPartial
    ? "Enter both the high and the low, or leave both blank."
    : windowInverted
      ? "The low cannot be above the high."
      : closeOutside
        ? "The closing price has to sit between the low and the high."
        : null;

  const canSubmit = validPrice && validStart && windowOk && !saving;

  const outcomeFor = (row) => {
    if (!validPrice) return null;
    const lo = Math.min(Number(row.range_min), Number(row.range_max));
    const hi = Math.max(Number(row.range_min), Number(row.range_max));
    return {
      inRange: typed >= lo && typed <= hi,
      touched: lowNum !== null && highNum !== null ? highNum >= lo && lowNum <= hi : null,
    };
  };

  const onSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await settleResearch(prediction.id, {
        startPrice: startNum,
        actualPrice: typed,
        actualLow: lowNum,
        actualHigh: highNum,
      });
      onSettled?.(data?.data || data);
      toggle();
    } catch (e) {
      setError(e?.error || e?.message || (typeof e === "string" ? e : "Could not save the settlement."));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => (Number.isFinite(Number(n)) ? `$${Number(n).toFixed(2)}` : "—");

  /**
   * The instant being asked about.
   *
   * Without it "Closing price" is ambiguous on anything shorter than a day: a
   * 4-hour SOXL forecast closed at a particular minute, and the number wanted
   * is the price AT that minute, not the last one on the chart. Getting it
   * wrong scores the forecast against the wrong moment, and nothing downstream
   * can detect that.
   *
   * Local time, because that is the clock the member's broker or chart is
   * showing while they read it off. The rest of the app prints UTC, but this is
   * the one place the number is being copied from somewhere else.
   */
  const at = (value, opts) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString(undefined, opts);
  };

  const FULL = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  const SHORT = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };

  const closedLabel = at(prediction?.valid_until, FULL);
  // Short form for the field sub-line; the sentence above already carries the
  // full date, so repeating the year under the input only makes it wrap.
  const closedShort = at(prediction?.valid_until, SHORT);
  // When the forecast was made. Naming it beside the field removes the
  // ambiguity in "price when asked" -- asked at which moment.
  const openedShort = at(prediction?.input_time, SHORT);

  /**
   * The stretch of chart the high and the low are read from.
   *
   * On each field rather than only in the note below it: "Window low" alone
   * does not say which window, and the obvious wrong answer — the day's low —
   * is one a member could supply without ever noticing the question. Short
   * format, no year, because it has to sit under a label in a third of a row.
   */
  const windowRange = (() => {
    const from = at(prediction?.input_time, SHORT);
    const to = at(prediction?.valid_until, SHORT);
    return from && to ? `${from} → ${to}` : null;
  })();

  /**
   * Why a "4h" window can span most of a day.
   *
   * An hourly horizon counts TRADING hours, not clock hours: a 4h forecast made
   * at 2:30pm uses 1.5 of them before the close and picks the remaining 2.5 up
   * the next morning, ending at noon — four trading hours across 21.5 hours of
   * wall clock. The dialog showed both timestamps and no explanation, which
   * reads as the dates being wrong.
   *
   * Said only when the two actually diverge. Inside a single session the note
   * would be noise, and a daily horizon is counted in calendar days anyway.
   */
  const overnightNote = (() => {
    const m = String(prediction?.timeframe || '').match(/^(\d+)h$/);
    if (!m || !prediction?.input_time || !prediction?.valid_until) return null;
    const nominal = Number(m[1]);
    const wall = (new Date(prediction.valid_until) - new Date(prediction.input_time)) / 3600000;
    if (!Number.isFinite(wall) || wall < nominal * 1.5) return null;
    return `That is ${nominal} trading hours. The market was shut in between and closed time does not count, so the window covers more of the clock than it sounds like.`;
  })();
  const pct = validPrice && baseline
    ? ((typed - baseline) / baseline) * 100
    : null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        Grade {prediction.symbol} · {prediction.timeframe}
      </ModalHeader>
      <ModalBody>
        {closedLabel && (
          <p className="fs-13 mb-2">
            The window closed <strong>{closedLabel}</strong>. Enter the price at
            that moment.
          </p>
        )}
        <p className="text-muted fs-12">
          Baseline {fmt(baseline)} when the forecast was made.
          {group.length > 1 && ` ${group.length} models answered this question — one price settles them all.`}
        </p>

        {error && <Alert color="danger" className="fs-12 py-2">{error}</Alert>}

        <Row className="g-2">
          <Col sm={3}>
            <FormGroup>
              <Label for="settleStart" className="mb-1 d-block">
                Price when asked <span className="text-danger fw-normal fs-11">(required)</span>
                {openedShort && (
                  <span className="d-block fw-normal text-muted fs-11">at {openedShort}</span>
                )}
              </Label>
              <Input id="settleStart" type="number" step="any" min="0" value={start}
                     autoFocus placeholder={suggestion ? String(suggestion) : "e.g. 119.59"}
                     onChange={(e) => setStart(e.target.value)} />
              {suggestion && !start && (
                <Button color="link" size="sm" className="p-0 mt-1 fs-11"
                        onClick={() => setStart(String(suggestion))}>
                  Use {suggestion} — what the models reported
                </Button>
              )}
            </FormGroup>
          </Col>
          <Col sm={3}>
            <FormGroup>
              {/* The same sub-line the others carry, so the inputs
                  sit on one baseline instead of this one riding a row higher.
                  It also repeats the moment right where the number is typed,
                  which is where it matters. */}
              <Label for="settlePrice" className="mb-1 d-block">
                Closing price <span className="text-danger fw-normal fs-11">(required)</span>
                {closedShort && (
                  <span className="d-block fw-normal text-muted fs-11">at {closedShort}</span>
                )}
              </Label>
              <Input id="settlePrice" type="number" step="any" min="0" value={price}
                     placeholder="e.g. 114.29"
                     onChange={(e) => setPrice(e.target.value)} />
            </FormGroup>
          </Col>
          <Col sm={3}>
            <FormGroup>
              <Label for="settleLow" className="mb-1 d-block">
                Window low <span className="text-muted fw-normal fs-11">(optional)</span>
                {windowRange && (
                  <span className="d-block fw-normal text-muted fs-11">{windowRange}</span>
                )}
              </Label>
              <Input id="settleLow" type="number" step="any" min="0" value={low}
                     onChange={(e) => setLow(e.target.value)} />
            </FormGroup>
          </Col>
          <Col sm={3}>
            <FormGroup>
              <Label for="settleHigh" className="mb-1 d-block">
                Window high <span className="text-muted fw-normal fs-11">(optional)</span>
                {windowRange && (
                  <span className="d-block fw-normal text-muted fs-11">{windowRange}</span>
                )}
              </Label>
              <Input id="settleHigh" type="number" step="any" min="0" value={high}
                     onChange={(e) => setHigh(e.target.value)} />
            </FormGroup>
          </Col>
        </Row>

        {overnightNote && (
          <div className="text-muted fs-12 mb-2">{overnightNote}</div>
        )}

        <div className="text-muted fs-12 mb-2">
          The high and low record whether price traded inside a forecast range
          even when it closed outside. Leave them blank and the close alone is
          used.
        </div>

        {localError && <div className="text-danger fs-12 mb-2">{localError}</div>}

        {validPrice && !localError && (
          <div className="fs-12 mb-3 text-muted">
            {typed > baseline ? "Up" : "Down"}
            {pct !== null && ` ${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`} from baseline
          </div>
        )}

        {/* What this settlement will produce, before it is saved.
            Read-only: the outcome follows from the price, so it is worth
            showing and not worth asking about. */}
        {validPrice && !localError && group.length > 0 && (
          <div className="vstack gap-1">
            {group.map((row) => {
              const o = outcomeFor(row);
              const lo = Math.min(Number(row.range_min), Number(row.range_max));
              const hi = Math.max(Number(row.range_min), Number(row.range_max));
              return (
                <div key={row.id} className="d-flex justify-content-between align-items-center fs-12">
                  <span>
                    <strong>{modelNameOf(row)}</strong>
                    <span className="text-muted ms-2">{fmt(lo)} – {fmt(hi)}</span>
                  </span>
                  <span>
                    <Badge color={o.inRange ? "success" : "danger"}>
                      {o.inRange ? "In range" : "Missed"}
                    </Badge>
                    {!o.inRange && o.touched && (
                      <Badge color="warning" className="ms-1">Touched</Badge>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={saving}>Cancel</Button>
        <Button color="primary" onClick={onSubmit} disabled={!canSubmit}>
          {saving
            ? <><Spinner size="sm" /> Saving…</>
            : `Score ${group.length > 1 ? `${group.length} forecasts` : "this forecast"}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SettleModal;
